// src/sectors/pilates/sessions/modals/MemberPackageCard.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { fmtDate, safeNum, calcRem } from './packageUtils';

export function MemberPackageCard({
  tokens,
  memberId,
  pkgState,
  consumeType, // "NORMAL" | "TELAFI"
  chosenItemId, // string
  onRemove,
  onCreatePackage, // (memberId)=>void
}) {
  const theme = useMuiTheme();
  const surface = tokens?.surface || '#fff';
  const isDark = !!tokens?.isDark;

  const { pkg, items, loading, error } = pkgState || {};
  const title = pkg?.planName || 'Aktif Paket';
  const startAt = pkg?.startAt;
  const endAt = pkg?.estimatedEndAt || pkg?.estimatedEnd || pkg?.endAt || null;
  const cap = pkg?.classCapacity;

  const list = Array.isArray(items) ? items : [];
  const chosen = list.find((x) => String(x.id) === String(chosenItemId)) || list[0] || null;

  const normalTotal = chosen ? safeNum(chosen.totalSessions) : null;
  const normalUsed = chosen ? safeNum(chosen.usedSessions) : null;
  const normalRem = calcRem(normalTotal, normalUsed);

  const makeUpTotal = chosen ? safeNum(chosen.totalMakeUp) : null;
  const makeUpUsed = chosen ? safeNum(chosen.usedMakeUp) : null;
  const makeUpRem = calcRem(makeUpTotal, makeUpUsed);

  const shown =
    consumeType === 'TELAFI'
      ? { total: makeUpTotal, used: makeUpUsed, rem: makeUpRem }
      : { total: normalTotal, used: normalUsed, rem: normalRem };

  const showCreateCTA = !loading && (error || !pkg);

  return (
    <MDBox
      sx={{
        borderRadius: '20px',
        border: `1px solid ${tokens?.border || theme.palette.divider}`,
        background: surface, // ✅ gri yok
        p: 1.1,
      }}
    >
      <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <MDBox sx={{ minWidth: 0 }}>
          <MDBox display="flex" alignItems="center" gap={0.7} sx={{ flexWrap: 'wrap' }}>
            <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              {loading ? 'Paket yükleniyor…' : showCreateCTA ? 'Paket yok' : title}
            </MDTypography>

            {showCreateCTA ? (
              <MDButton
                variant="gradient"
                color="success"
                size="small"
                onClick={() => onCreatePackage?.(String(memberId))}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 900,
                  px: 1.2,
                  minHeight: 34,
                }}
              >
                <Icon sx={{ fontSize: 18, mr: 0.5 }}>add</Icon>
                Paket Oluştur
              </MDButton>
            ) : null}

            <MDButton
              variant="text"
              color="secondary"
              onClick={() => onRemove?.(memberId)}
              sx={{ minWidth: 34, width: 34, height: 34, borderRadius: '12px', opacity: 0.8 }}
            >
              <Icon sx={{ fontSize: 18 }}>close</Icon>
            </MDButton>
          </MDBox>

          <MDTypography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.25 }}>
            Üye #{memberId}
            {cap != null ? ` • Kapasite: ${cap}` : ''}
            {startAt ? ` • Başlangıç: ${fmtDate(startAt)}` : ''}
            {endAt ? ` • Bitiş: ${fmtDate(endAt)}` : ''}
          </MDTypography>

          {!loading && !showCreateCTA && chosen ? (
            <MDTypography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.35 }}>
              Aktivite: <b>{chosen.activityName}</b>
              {consumeType === 'TELAFI' ? ' • (Telafi)' : ' • (Paket)'}
            </MDTypography>
          ) : null}
        </MDBox>

        {!loading && !showCreateCTA && pkg ? (
          <MDBox sx={{ opacity: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon sx={{ fontSize: 18 }}>verified</Icon>
            <MDTypography variant="caption">Aktif</MDTypography>
          </MDBox>
        ) : null}
      </MDBox>

      {!loading && !showCreateCTA && chosen ? (
        <MDBox sx={{ mt: 0.9, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.8 }}>
          {[
            ['Hak', shown.total, 'info'],
            ['Kullanılan', shown.used, 'warning'],
            ['Kalan', shown.rem, 'success'],
          ].map(([label, val, tone]) => (
            <MDBox
              key={label}
              sx={{
                p: 1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.grey[500], isDark ? 0.18 : 0.22)}`,
                background: alpha(theme.palette[tone].main, isDark ? 0.1 : 0.07),
              }}
            >
              <MDTypography variant="caption" sx={{ opacity: 0.78, fontWeight: 900 }}>
                {label}
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                {val == null ? '—' : val}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      ) : null}
    </MDBox>
  );
}
